/** Shared helpers for instanced glTF vegetation and props: node materials with wind, parts per LOD node. */
import * as THREE from 'three/webgpu';
import { dFdx, dFdy, log2, uv, interleavedGradientNoise, screenCoordinate, smoothstep, cameraPosition, dot, float, hash, instanceIndex, max, normalize, positionLocal, positionWorld, pow, sin, cos, texture, time, uniform, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { textureSize } from './textureRelease';

export interface Part { geometry: THREE.BufferGeometry; material: THREE.Material }

/**
 * Copy with float attributes. meshopt-quantised glTF stores positions as normalised int16 with the scale on the
 * node; baking a transform into such an attribute would clamp it to [-1, 1].
 */
export function toFloat(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  for (const [name, a] of Object.entries(src.attributes)) {
    const attr = a as THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
    const out = new Float32Array(attr.count * attr.itemSize);
    for (let i = 0; i < attr.count; i++) for (let c = 0; c < attr.itemSize; c++) out[i * attr.itemSize + c] = attr.getComponent(i, c);
    g.setAttribute(name, new THREE.BufferAttribute(out, attr.itemSize));
  }
  if (src.index) g.setIndex(src.index.clone());
  return g;
}

/** The meshes under a named node of a glTF scene, with their transforms baked into the geometry. */
export function partsOf(scene: THREE.Object3D, name: string): Part[] {
  const node = scene.getObjectByName(name);
  if (!node) throw new Error(`glTF node ${name} missing`);
  node.updateMatrixWorld(true);
  // Relative to the node's parent: the node's own matrix carries meshopt's dequantisation scale.
  const inv = node.parent ? node.parent.matrixWorld.clone().invert() : new THREE.Matrix4();
  const out: Part[] = [];
  node.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const g = toFloat(o.geometry as THREE.BufferGeometry);
    g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, o.matrixWorld));
    if (!g.attributes.uv) addBoxUv(g);
    out.push({ geometry: g, material: o.material as THREE.Material });
  });
  return out;
}

/**
 * Box-projected UVs (1 repeat per metre) for a part exported without texture coordinates whose material still
 * samples maps (the broadleaf branches): otherwise every pass warns 'Vertex attribute "uv" not found'.
 */
export function addBoxUv(g: THREE.BufferGeometry): void {
  const p = g.attributes.position!, n = g.attributes.normal;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const ax = n ? Math.abs(n.getX(i)) : 0, az = n ? Math.abs(n.getZ(i)) : 1;
    uv[i * 2] = ax > az ? z : x;
    uv[i * 2 + 1] = y;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** 1: foliage sways with the wind, 0: still (the `wind` world module). */
export const windEnabled = uniform(1);

export interface WindOptions { amplitude: number; flutter: number; sunDir?: Node<'vec3'>; translucency?: number }

/** Converts a glTF material to a node material with per-instance wind sway (and optional leaf translucency). */
export function windMaterial(src: THREE.Material, o: WindOptions): THREE.MeshStandardNodeMaterial {
  const s = src as THREE.MeshStandardMaterial;
  const m = new THREE.MeshStandardNodeMaterial();
  m.setValues({
    map: s.map, normalMap: s.normalMap, roughnessMap: s.roughnessMap, metalnessMap: s.metalnessMap, aoMap: s.aoMap,
    color: s.color, roughness: s.roughness, metalness: Math.min(s.metalness, 0.05),
    side: s.side, normalScale: s.normalScale,
  });
  m.transparent = false;
  cutout(m, s.map, s.alphaTest || (s.transparent ? 0.45 : 0));
  if (o.amplitude > 0 || o.flutter > 0) {
    // phaseSeed: per instance (default its slot). setWindPhase rebuilds the sway with another seed.
    const swayWith = (phaseSeed: Node<'float'> | Node<'uint'>) => {
      const phase = hash(phaseSeed).mul(6.283);
      const h = positionLocal.y.max(0);
      const bend = h.mul(h).mul(o.amplitude).mul(windEnabled);
      const wind = (t: Node<'float'>) => {
        const sway = vec3(sin(t.mul(1.1).add(phase)), 0, cos(t.mul(0.83).add(phase.mul(1.7)))).mul(bend);
        const flutter = sin(t.mul(5.3).add(positionLocal.x.mul(3.1)).add(positionLocal.z.mul(2.3)).add(phase)).mul(o.flutter).mul(h.min(4).div(4)).mul(windEnabled);
        return sway.add(vec3(flutter, flutter.mul(0.5), flutter.mul(0.7)));
      };
      return positionLocal.add(wind(time));
    };
    m.positionNode = swayWith(instanceIndex);
    m.userData.windSway = swayWith;
  }
  if (o.sunDir && o.translucency) {
    // Back-lit leaves glow: view toward the sun through the foliage.
    const v = normalize(positionWorld.sub(cameraPosition));
    const back = pow(max(dot(v, o.sunDir), 0), 5).mul(o.translucency);
    m.emissiveNode = vec3(0.55, 0.75, 0.25).mul(back);
  }
  return m;
}

/**
 * Alpha cut-outs. With MSAA on (see setAlphaCoverage) the main pass uses alpha-to-coverage: the cut edge becomes
 * a sub-pixel coverage ramp instead of a hard stair-stepped (and, in motion, shimmering) edge. The shadow pass keeps
 * a hard cut through `maskShadowNode`. Neither path uses the numeric `alphaTest`: the shadow pass renders every
 * caster with one shared override material and copies each caster's alphaTest onto it; crossing between 0 and
 * non-zero bumps that material's version, which makes three re-derive the cache key of every shadow draw that
 * frame (measured: ~18% of frame CPU with foliage and opaque casters interleaved).
 *
 * Coverage-preserving mips: a mip level averages leaf texels with the transparent gaps around them, so alpha
 * drops with distance and a fixed threshold thins far foliage into speckles. The alpha is scaled up with the mip
 * level the pixel samples (Golus, "Anti-aliased alpha test"), which keeps the covered area roughly constant.
 */
let alphaCoverage = false;
/** Set before building materials: true when the scene pass is multisampled (tier.msaa > 0). */
export function setAlphaCoverage(on: boolean): void { alphaCoverage = on; }

/**
 * Foliage depth prepass. Layered alpha-tested leaf cards made the lit foliage shader run several times per pixel
 * (overdraw; the Iris Xe is fill-rate bound). A depth-only twin of each cut-out draw (same geometry, same shared
 * instance buffer, same position and mask nodes, no colour) lays down the nearest leaf depth first; the lit draw then
 * tests EQUAL without writing depth, so it shades each covered pixel once. Same image: the lit pass keeps its own
 * mask, and what passes EQUAL is exactly the nearest fragment the lit pass alone would have kept.
 * Not with alpha-to-coverage (MSAA tiers): a hard-cut depth mask would not match the lit pass's partial coverage.
 */
export const PREPASS_ORDER = 1, PREPASS_LIT_ORDER = 2;
export function prepassApplies(m: THREE.Material): boolean {
  const n = m as THREE.NodeMaterial;
  return !alphaCoverage && !m.transparent && !!n.maskNode && !!m.userData.cutMask;
}
/** The depth-only twin of a cut-out node material. */
/**
 * Re-seeds a wind material's per-instance sway phase (windMaterial; the instance slot by default). Its depth twins
 * share the position node and must be passed too, or their depth no longer matches. Recompiles them.
 */
export function setWindPhase(m: THREE.NodeMaterial, seed: Node<'float'> | Node<'uint'>, twins: THREE.NodeMaterial[] = []): void {
  const sway = m.userData.windSway as ((s: Node<'float'> | Node<'uint'>) => Node<'vec3'>) | undefined;
  if (!sway) return;
  m.positionNode = sway(seed);
  m.needsUpdate = true;
  for (const t of twins) { t.positionNode = m.positionNode; t.needsUpdate = true; }
}

export function depthOnlyMaterial(src: THREE.NodeMaterial): THREE.MeshBasicNodeMaterial {
  const d = new THREE.MeshBasicNodeMaterial({ side: src.side });
  d.name = 'foliage-depth';
  d.colorWrite = false;
  d.positionNode = src.positionNode;
  d.maskNode = src.maskNode;
  return d;
}
/**
 * Switches a lit cut-out between the plain path and "after the prepass" (depth EQUAL, no depth write). After the
 * prepass the lit draw also drops its mask (`stripMask`): EQUAL already limits it to the pixels where its depth twin,
 * which keeps the mask (cut-out and LOD dither), wrote this surface's depth. No alpha fetch, no derivatives and no
 * discard in the lit shader. The shadow pass keeps the mask (maskShadowNode).
 */
export function setPrepassLit(m: THREE.Material, on: boolean, stripMask = true): void {
  m.depthFunc = on ? THREE.EqualDepth : THREE.LessEqualDepth;
  m.depthWrite = !on;
  const n = m as THREE.NodeMaterial, u = m.userData as { litMask?: Node<'bool'> | null; shadowFromLit?: boolean };
  if (on && stripMask && n.maskNode && u.litMask === undefined) {
    u.litMask = n.maskNode as Node<'bool'>;
    if (!n.maskShadowNode) { n.maskShadowNode = n.maskNode; u.shadowFromLit = true; }
    n.maskNode = null;
  } else if ((!on || !stripMask) && u.litMask !== undefined) {
    n.maskNode = u.litMask;
    if (u.shadowFromLit) n.maskShadowNode = null;
    delete u.litMask; delete u.shadowFromLit;
  }
  m.needsUpdate = true;
}

/** Alpha scaled by the sampled mip level of a `size`-texel texture at `coord` (0..1 texture coordinates). */
export function mipCoverageAlpha(a: Node<'float'>, coord: Node<'vec2'>, size: [number, number] | Node<'vec2'>): Node<'float'> {
  const t = coord.mul(Array.isArray(size) ? vec3(size[0], size[1], 0).xy : size);
  const dx = dFdx(t), dy = dFdy(t);
  const level = log2(max(dot(dx, dx), dot(dy, dy)).max(1e-8)).mul(0.5).max(0);
  return a.mul(level.mul(0.25).add(1)).min(1);
}


/**
 * Cuts `m` where `alpha` falls below `threshold`. `base` is the alpha the material already has (its colour map's,
 * or null for 1), which the opacity divides back out. `m.userData.cutMask` keeps the plain cut (no LOD dither).
 */
export function cutAlpha(m: THREE.NodeMaterial, alpha: Node<'float'>, threshold: number, base: Node<'float'> | null = null): void {
  m.alphaTest = 0;
  const cut = alpha.greaterThan(threshold);
  m.userData.cutMask = cut;
  m.userData.cutThreshold = threshold;
  if (!alphaCoverage) { m.maskNode = cut; return; }
  m.opacityNode = base ? alpha.div(max(base, 1e-3)) : alpha;
  m.alphaTestNode = float(threshold);
  m.alphaToCoverage = true;
  m.maskShadowNode = cut;
}

/** Cut-out from a colour map's alpha (glTF foliage). */
export function cutout(m: THREE.NodeMaterial, map: THREE.Texture | null | undefined, threshold: number): void {
  m.alphaTest = 0;
  if (threshold > 0 && map) cutAlpha(m, mipCoverageAlpha(texture(map).a, uv(), textureSize(map)), threshold, texture(map).a);
}

export const windTime = uniform(0);

/**
 * Screen-door cross-fade between two LODs around distance `at` (band LOD_BAND_M): true where the fragment is
 * kept. The near representation (`near` = true) keeps the pixels the far one drops, so the pair never leaves a
 * hole or draws a pixel twice.
 */
export const LOD_BAND_M = 12;
export function lodDither(d: Node<'float'>, at: number | Node<'float'>, near: boolean): Node<'bool'> {
  const t = typeof at === 'number' ? smoothstep(at - LOD_BAND_M / 2, at + LOD_BAND_M / 2, d) : smoothstep(at.sub(LOD_BAND_M / 2), at.add(LOD_BAND_M / 2), d);
  const noise = interleavedGradientNoise(screenCoordinate.xy);
  return near ? noise.greaterThanEqual(t) : noise.lessThan(t);
}

/**
 * The view camera's position, set every frame by PrettyView. LOD fades measure from it rather than from
 * cameraPosition, which in the shadow passes is the sun's camera (every caster would dither out of the shadows).
 */
export const viewCamera = uniform(new THREE.Vector3());
/** Horizontal distance of the fragment from the view camera. */
export const viewDistance = (): Node<'float'> => positionWorld.xz.sub(viewCamera.xz).length();

/**
 * Keeps a representation between two LOD switches: fades in around `from` (null = no near switch) and out around
 * `to` (null = no far switch), combined with any existing mask. The distances are uniforms (Forest.setLod).
 */
export function lodBand(m: THREE.NodeMaterial, from: Node<'float'> | null, to: Node<'float'> | null): void {
  const d = viewDistance();
  let keep: Node<'bool'> | null = null;
  if (from) keep = lodDither(d, from, false);
  if (to) { const k = lodDither(d, to, true); keep = keep ? keep.and(k) : k; }
  if (!keep) return;
  m.maskNode = m.maskNode ? (m.maskNode as Node<'bool'>).and(keep) : keep;
  if (m.maskShadowNode) m.maskShadowNode = (m.maskShadowNode as Node<'bool'>).and(keep);
}
